import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Picker `value` from an ISO string, stable across renders. MUI X re-centers the calendar on the
 * value's month whenever the value *reference* changes, so an inline `dayjs(iso)` snaps month
 * navigation back to the selected date on every parent re-render.
 */
export const usePickerValue = (iso: string | null | undefined) => useMemo(() => (iso ? dayjs(iso) : null), [iso]);

/** Matches `CustomSelect`: gray-300 border, gray-400 hover, blue-500 + soft ring on focus */
const border = '#d1d5db';
const borderHover = '#9ca3af';
const borderFocus = '#3b82f6';
const ringFocus = '0 0 0 3px rgba(59, 130, 246, 0.12)';
const paperBorder = '#e5e7eb';
const shadowMd = '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.08)';

/**
 * Collapsed field — match `CustomSelect` trigger: `text-sm`, `rounded-lg`, `p-2`-ish padding.
 * (MUI X v8 uses PickersOutlinedInput + section list, not `MuiOutlinedInput`.)
 */
export const gentlePickerTextFieldSx: SxProps<Theme> = {
    '& .MuiPickersOutlinedInput-root': {
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        backgroundColor: '#fff',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        paddingLeft: '8px',
        paddingRight: '8px',
    },
    '& .MuiPickersOutlinedInput-root .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
        marginLeft: 0,
        marginRight: 0,
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: border,
    },
    '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: borderHover,
    },
    '& .MuiPickersOutlinedInput-root:focus-within .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: borderFocus,
        borderWidth: 1,
    },
    '& .MuiPickersOutlinedInput-root:focus-within': {
        boxShadow: ringFocus,
    },
    '& .MuiPickersSectionList-root': {
        fontSize: '0.875rem',
        lineHeight: 1.43,
    },
    '& .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent': {
        fontSize: '0.875rem',
    },
    /* Fallback: classic single `<input />` field (`enableAccessibleFieldDOMStructure={false}`) */
    '& .MuiOutlinedInput-root': {
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        backgroundColor: '#fff',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '& fieldset': {
            borderColor: border,
            borderWidth: 1,
        },
        '&:hover fieldset': {
            borderColor: borderHover,
        },
        '&.Mui-focused fieldset': {
            borderColor: borderFocus,
            borderWidth: 1,
        },
        '&.Mui-focused': {
            boxShadow: ringFocus,
        },
    },
    '& .MuiOutlinedInput-input': {
        fontSize: '0.875rem',
        padding: '8px 12px',
    },
};

/** Softer analog clock + layout accents (default MUI clock can feel heavy) */
export const gentleDateTimePickerRootSx: SxProps<Theme> = {
    '& .MuiClockPointer-root': {
        backgroundColor: '#3b82f6',
        opacity: 0.9,
    },
    '& .MuiClockPointer-thumb': {
        border: '2px solid #fff',
        backgroundColor: '#3b82f6',
        boxShadow: '0 1px 2px rgb(0 0 0 / 0.08)',
    },
    '& .MuiClock-pin': {
        backgroundColor: '#3b82f6',
    },
    '& .MuiClock-amButton, & .MuiClock-pmButton': {
        borderRadius: '0.5rem',
        textTransform: 'none',
        fontWeight: 500,
    },
};

export const gentleDateTimePickerSlotProps = {
    textField: {
        size: 'small' as const,
        fullWidth: true,
        sx: gentlePickerTextFieldSx,
    },
    openPickerButton: {
        size: 'small' as const,
        sx: {
            color: '#9ca3af',
            padding: '4px',
            marginRight: 0,
            '&:hover': { backgroundColor: '#f9fafb', color: '#6b7280' },
            '& .MuiSvgIcon-root': {
                fontSize: '1rem',
            },
        },
    },
    desktopPaper: {
        elevation: 0,
        sx: {
            borderRadius: '0.5rem',
            border: `1px solid ${paperBorder}`,
            boxShadow: shadowMd,
            overflow: 'hidden',
        },
    },
    toolbar: {
        sx: {
            backgroundColor: '#ffffff',
            '& .MuiTypography-root': { color: '#374151', fontWeight: 500 },
            padding: '8px 12px',
        },
    },
    tabs: {
        sx: {
            minHeight: 48,
            borderBottom: `1px solid ${paperBorder}`,
            '& .MuiTab-root': {
                textTransform: 'none' as const,
                fontSize: '0.875rem',
                fontWeight: 500,
                minHeight: 48,
                color: '#6b7280',
                '&.Mui-selected': {
                    color: '#1d4ed8',
                },
            },
            '& .MuiTabs-indicator': {
                backgroundColor: '#3b82f6',
                height: 2,
                borderRadius: 1,
            },
        },
    },
    day: {
        sx: {
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 400,
            '&.Mui-selected': {
                backgroundColor: '#dbeafe',
                color: '#1d4ed8',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#bfdbfe' },
                '&:focus': { backgroundColor: '#bfdbfe' },
            },
        },
    },
    actionBar: {
        sx: {
            padding: '8px 12px',
            borderTop: `1px solid ${paperBorder}`,
            backgroundColor: '#fafafa',
            '& .MuiButton-root': {
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '0.5rem',
            },
        },
    },
    mobilePaper: {
        elevation: 0,
        sx: {
            borderRadius: '0.75rem 0.75rem 0 0',
            borderTop: `1px solid ${paperBorder}`,
            boxShadow: shadowMd,
            overflow: 'hidden',
        },
    },
    dialog: {
        sx: {
            '& .MuiDialog-paper': {
                margin: 0,
                maxHeight: '100%',
            },
        },
    },
};
